import {cachedResponse,cacheable,rememberResponse,saveThroughContinuity,offlineReply,setConnectionUnavailable,connectionUnavailable,continuityReady,localRecordPending,cachedReadAvailable,resolveContinuityPath,setCacheAuthoriser,pendingForRecord} from './continuity';
// Typed API client for the Pamosi Clinic workspace. Thin fetch wrapper +
// domain-grouped methods. Every call routes to the pamosi-api Worker
// (config.apiBase). Auth tokens live in localStorage (see storage.ts).

import type {
    Clinic,
    ClinicActivityListRes,
    ClinicInviteListItem,
    ClinicMember,
    ClinicMemberRole,
    ClinicMemberWithClinician,
    Clinician,
    ClinicFacilityType,
    CreateClinicInviteRes,
    Diagnosis,
    Encounter,
    EncounterType,
    GetMyClinicRes,
    JoinClinicReq,
    JoinClinicRes,
    MedicationDispense,
    MedicationRequest,
    Observation,
    PatientCardType,
    PatientHmoCoverage,
    PatientRegistrationDetails,
    QueueListRes,
    QueueUpdateStatusRes,
    InboxListRes,
    MentionCandidatesRes,
    SharedInstructionRes,
    SharedInstructionsListRes,
    ReceptionAddBeneficiaryRes,
    ReceptionCreateFamilyRes,
    ReceptionCreatePatientRes,
    ReceptionDepartmentsRes,
    ReceptionEnqueueRes,
    ReceptionFamilyBundleRes,
    ReceptionFamilySearchRes,
    ReceptionHmoProvidersRes,
    ReceptionPatientSearchRes,
    ReceptionRegistrationBundleRes,
    ReceptionUpdateRegistrationReq,
    RegisterClinicReq,
    RegisterClinicRes,
    ProcedurePhotoAttachRes,
    ProcedurePhotoSignUploadReq,
    ProcedurePhotoSignUploadRes,
    ProceduresQueueRes,
    ServiceRequest,
    ServiceRequestCancelReq,
    ServiceRequestCloseReq,
    ServiceRequestFulfillmentReq,
    ServiceRequestFulfillmentRes,
    ServiceRequestFulfillmentsListRes,
    TriageRes,
    TriageUpsertReq,
    VisitQueueStatus,
    AdmissionView,
    AdmitRequest,
    AdmitResponse,
    AdmissionRes,
    AdmissionsListRes,
    AdmissionStatus,
    CancelAdmissionRequest,
    DischargeRequest,
    SetVitalsScheduleRequest,
    AdmissionObservationChartRes,
    AdmissionMarRes,
    AdministrationRes,
    RecordAdministrationRequest,
    CreateShiftReportRequest,
    UpdateShiftReportRequest,
    ShiftReportRes,
    ShiftReportsListRes,
    LabTest,
    LabTestRes,
    LabTestsListRes,
    LabRequestView,
    LabRequestRes,
    LabsQueueRes,
    LabStatus,
    OrderLabRequest,
    UpsertLabTestRequest,
    EnterLabResultRequest,
    ReleaseLabRequest,
    CancelLabRequest,
    EncounterCharge,
    CreateEncounterChargeRequest,
    ClinicProcedure,
    ClinicProceduresListRes,
    ClinicProcedureRes,
    UpsertClinicProcedureRequest,
    VerifyHmoCoverageRequest,
} from '@pamosi/shared';
import { config } from './config';
import { getAccessToken, clearSession } from './storage';

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly code: string,
        message?: string,
    ) {
        super(message ?? code);
    }
}

async function request<T>(
    path: string,
    init: RequestInit & { auth?: boolean; bearer?: string; onlineOnly?: boolean } = {},
): Promise<T> {
    const { auth = true, bearer, headers, onlineOnly=false, ...rest } = init;
    const method=(init.method??'GET').toUpperCase();
    if(auth&&!bearer&&!onlineOnly&&!path.includes('/offline/')) {
        try { const saved=await saveThroughContinuity(path,method,init.body); if(saved.handled)return saved.body as T; }
        catch(e){throw new ApiError(0,'device_save_failed',e instanceof Error?e.message:'Could not save on this device.');}
        if(method!=='GET'&&pendingForRecord(path,init.body))throw new ApiError(409,'pending_device_changes','Sync or review saved device changes for this record before completing this action.');
        if(method==='GET'&&localRecordPending(path)){try{return await cachedResponse(path) as T;}catch{}}
        if(!navigator.onLine || connectionUnavailable()) {try{return await offlineReply(path,method) as T;}catch(e){throw new ApiError(0,'connection_required',e instanceof Error?e.message:'Connection required.');}}
    }
    const finalHeaders = new Headers(headers);
    if (!finalHeaders.has('content-type') && init.body) {
        finalHeaders.set('content-type', 'application/json');
    }
    if (bearer) {
        // Explicit override — used mid-flow before we've called signIn (e.g.
        // the sign-up-and-join lane, which chains setPassword + join on a
        // just-issued OTP token that isn't in storage yet).
        finalHeaders.set('authorization', `Bearer ${bearer}`);
    } else if (auth) {
        const token = getAccessToken();
        if (token) finalHeaders.set('authorization', `Bearer ${token}`);
    }

    // AbortController with 20s timeout — the Worker responds in <200ms in
    // practice, so anything longer is a stuck connection or a Cloudflare
    // retry loop. We'd rather surface an error than leave the UI spinning
    // forever on a wedged request.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), method==='GET'&&!onlineOnly&&cachedReadAvailable(path)?2500:20_000);
    let res: Response;
    try {
        res = await fetch(`${config.apiBase}${resolveContinuityPath(path)}`, {
            ...rest,
            headers: finalHeaders,
            signal: controller.signal,
        });
    } catch (err) {
        setConnectionUnavailable(true);
        if(auth&&!bearer&&!onlineOnly&&method==='GET'&&cacheable(path)){try{return await cachedResponse(path) as T;}catch{ /* Preserve network error when no authorised snapshot exists. */ }}
        if (err instanceof DOMException && err.name === 'AbortError') {
            throw new ApiError(0, 'network_timeout', 'Request timed out. Check connection and try again.');
        }
        throw new ApiError(0, 'network_error', err instanceof Error ? err.message : 'Network error');
    } finally {
        clearTimeout(timeoutId);
    }

    setConnectionUnavailable(res.status>=500);
    if(res.status>=500&&auth&&!bearer&&!onlineOnly&&method==='GET'&&cacheable(path)){try{return await cachedResponse(path) as T;}catch{}}
    if (res.status === 401 && auth) {
        window.dispatchEvent(new Event('pamosi-offline-lock'));
        // Session gone. Clear and let the router redirect to /login on the
        // next render (auth context reads storage on mount).
        clearSession();
    }

    const contentType = res.headers.get('content-type') ?? '';
    const body: unknown = contentType.includes('application/json')
        ? await res.json().catch(() => ({}))
        : await res.text();

    if(res.status===404&&auth&&/\/encounters\/[\da-f-]+\/triage$/.test(path)){try{await rememberResponse(path,{triage:null});}catch{}}
    if (!res.ok) {
        const code = (body && typeof body === 'object' && 'error' in body
            ? String((body as { error: unknown }).error)
            : 'request_failed');
        throw new ApiError(res.status, code);
    }
    if(auth&&!bearer&&method==='GET'&&cacheable(path)&&await continuityReady()){
        try{await rememberResponse(path,body);if(!onlineOnly)return await cachedResponse(path) as T;}catch(e){window.dispatchEvent(new CustomEvent('pamosi-device-cache-error',{detail:e instanceof Error?e.message:'Device saving unavailable.'}));}
    }
    return body as T;
}

// ─── Auth ────────────────────────────────────────────────────────────────

export interface OtpRequestRes { sent: boolean; expires_in_seconds: number }
export interface OtpVerifyRes {
    access_token: string;
    refresh_token: string;
    clinician: Clinician;
}

export interface PasswordAuthRes {
    access_token: string;
    refresh_token: string;
    clinician: Clinician;
}
export interface ResetRequestRes { sent: true; phone_hint: string }

// Email-OTP register response — server returns tokens + clinician + fresh
// clinic + admin membership in one atomic call.
export interface EmailOtpVerifyRegisterRes {
    access_token: string;
    refresh_token: string;
    clinician: Clinician;
    clinic: Clinic;
    member: ClinicMember;
}

export const authApi = {
    meOnline:()=>request<{clinician:Clinician}>('/clinician/v1/me',{onlineOnly:true}),
    signupWithInvite(args: {code:string;fullName:string;email:string;phone:string;username:string;password:string;deviceUuid:string}) {
        return request<PasswordAuthRes>('/clinician/v1/auth/invite-signup', { method:'POST', auth:false,
            body:JSON.stringify({code:args.code.trim().toUpperCase(),full_name:args.fullName.trim(),email:args.email.trim(),phone_e164:args.phone.trim(),username:args.username.trim(),password:args.password,device_uuid:args.deviceUuid}) });
    },
    requestOtp(phoneE164: string) {
        return request<OtpRequestRes>('/clinician/v1/auth/otp/request', {
            method: 'POST',
            auth: false,
            body: JSON.stringify({ phone_e164: phoneE164 }),
        });
    },
    verifyOtp(args: {
        phoneE164: string;
        code: string;
        deviceUuid: string;
    }) {
        return request<OtpVerifyRes>('/clinician/v1/auth/otp/verify', {
            method: 'POST',
            auth: false,
            body: JSON.stringify({
                phone_e164: args.phoneE164,
                code: args.code,
                device_uuid: args.deviceUuid,
                platform: 'android', // API requires ios|android — web treats itself as android for now
            }),
        });
    },
    // Username/password lane — parallel to OTP. Same session-token shape,
    // same clinician_devices row on the server.
    loginPassword(args: { username: string; password: string; deviceUuid: string }) {
        return request<PasswordAuthRes>('/clinician/v1/auth/password/login', {
            method: 'POST',
            auth: false,
            body: JSON.stringify({
                username: args.username,
                password: args.password,
                device_uuid: args.deviceUuid,
                platform: 'android',
            }),
        });
    },
    requestPasswordReset(username: string) {
        return request<ResetRequestRes>('/clinician/v1/auth/password/reset/request', {
            method: 'POST',
            auth: false,
            body: JSON.stringify({ username }),
        });
    },
    verifyPasswordReset(args: {
        username: string;
        code: string;
        newPassword: string;
        deviceUuid: string;
    }) {
        return request<PasswordAuthRes>('/clinician/v1/auth/password/reset/verify', {
            method: 'POST',
            auth: false,
            body: JSON.stringify({
                username: args.username,
                code: args.code,
                new_password: args.newPassword,
                device_uuid: args.deviceUuid,
                platform: 'android',
            }),
        });
    },
    // Authed: used by the migration prompt after an OTP login when the
    // clinician has no username yet. Optional `bearer` override lets the
    // sign-up-and-join flow use a just-issued OTP token before calling
    // signIn (which is what stashes tokens in localStorage).
    setPassword(args: { username: string; password: string; bearer?: string }) {
        return request<{ clinician: Clinician }>('/clinician/v1/auth/password/set', {
            method: 'POST',
            body: JSON.stringify({ username: args.username, password: args.password }),
            ...(args.bearer ? { bearer: args.bearer } : {}),
        });
    },
    me() {
        return request<{ clinician: Clinician }>('/clinician/v1/me');
    },

    // ─── Email OTP (pre-auth workspace registration) ──────────────────
    // These are the only auth calls that never touch phone OTP. Powers the
    // new /register-workspace flow: request → verify-register (which also
    // creates the clinic + admin membership + mints tokens in one shot).
    requestEmailOtp(email: string) {
        return request<OtpRequestRes>('/clinician/v1/auth/email-otp/request', {
            method: 'POST',
            auth: false,
            body: JSON.stringify({ email }),
        });
    },
    verifyEmailOtpAndRegister(args: {
        email: string;
        code: string;
        fullName: string;
        username: string;
        password: string;
        facilityName: string;
        facilityType: ClinicFacilityType;
        businessPhoneE164: string;
        licenseNumber: string;
        workspaceEmail: string;
        physicalAddress: string;
        country: string;
        state: string;
        lga: string;
        deviceUuid: string;
    }) {
        return request<EmailOtpVerifyRegisterRes>(
            '/clinician/v1/auth/email-otp/verify-register',
            {
                method: 'POST',
                auth: false,
                body: JSON.stringify({
                    email: args.email,
                    code: args.code,
                    full_name: args.fullName,
                    username: args.username,
                    password: args.password,
                    facility_name: args.facilityName,
                    facility_type: args.facilityType,
                    business_phone_e164: args.businessPhoneE164,
                    license_number: args.licenseNumber,
                    workspace_email: args.workspaceEmail,
                    physical_address: args.physicalAddress,
                    country: args.country,
                    state: args.state,
                    lga: args.lga,
                    device_uuid: args.deviceUuid,
                }),
            },
        );
    },
};

// ─── Clinic membership ───────────────────────────────────────────────────

export const clinicApi = {
    myClinicOnline:()=>request<GetMyClinicRes>('/clinic/v1/me',{onlineOnly:true}),
    myClinic() {
        return request<GetMyClinicRes>('/clinic/v1/me');
    },
    // Admin-only. 403 if the caller isn't admin — App shell hides the nav
    // entry off-role so this shouldn't normally surface.
    activity() {
        return request<ClinicActivityListRes>('/clinic/v1/activity');
    },
    members() {
        return request<{ members: ClinicMemberWithClinician[] }>('/clinic/v1/members');
    },
    invites() {
        return request<{ invites: ClinicInviteListItem[] }>('/clinic/v1/invites');
    },
    createInvite(args: {
        role: Exclude<ClinicMemberRole, 'provider'>;
        nameHint?: string;
    }) {
        return request<CreateClinicInviteRes>('/clinic/v1/invites', {
            method: 'POST',
            body: JSON.stringify({
                role: args.role,
                ...(args.nameHint ? { name_hint: args.nameHint } : {}),
            }),
        });
    },
    // Admin-only. Server refuses if it would strand the clinic (last admin).
    changeRole(clinicianId: string, role: Exclude<ClinicMemberRole, 'provider'>) {
        return request<{ member: ClinicMemberWithClinician['member'] }>(
            `/clinic/v1/members/${clinicianId}`,
            { method: 'PATCH', body: JSON.stringify({ role }) },
        );
    },
    // Admin-only soft-revoke. Server refuses to remove the sole admin.
    removeMember(clinicianId: string) {
        return request<{ ok: true }>(`/clinic/v1/members/${clinicianId}`, {
            method: 'DELETE',
        });
    },
    // Admin-only. Flips pending → expired. 404 if already redeemed / expired.
    revokeInvite(inviteId: string) {
        return request<{ ok: true }>(`/clinic/v1/invites/${inviteId}/revoke`, {
            method: 'POST',
        });
    },
    join(args: { code: string; fullName?: string; bearer?: string }) {
        const body: JoinClinicReq = {
            code: args.code,
            ...(args.fullName ? { full_name: args.fullName } : {}),
        };
        return request<JoinClinicRes>('/clinic/v1/join', {
            method: 'POST',
            body: JSON.stringify(body),
            ...(args.bearer ? { bearer: args.bearer } : {}),
        });
    },
    register(args: {
        fullName: string;
        adminEmail: string;
        facilityName: string;
        facilityType: ClinicFacilityType;
        businessPhoneE164: string;
        cacRcNumber?: string;
        licenseNumber: string;
        workspaceEmail: string;
        physicalAddress: string;
        country: string;
        state: string;
        lga: string;
        // Bearer override for the pre-auth register flow: after OTP verify
        // we hold the tokens in memory and chain setPassword + register on
        // that bearer before signIn persists them.
        bearer?: string;
    }) {
        const body: RegisterClinicReq = {
            full_name: args.fullName,
            admin_email: args.adminEmail,
            facility_name: args.facilityName,
            facility_type: args.facilityType,
            business_phone_e164: args.businessPhoneE164,
            ...(args.cacRcNumber ? { cac_rc_number: args.cacRcNumber } : {}),
            license_number: args.licenseNumber,
            workspace_email: args.workspaceEmail,
            physical_address: args.physicalAddress,
            country: args.country,
            state: args.state,
            lga: args.lga,
        };
        return request<RegisterClinicRes>('/clinic/v1/register', {
            method: 'POST',
            body: JSON.stringify(body),
            ...(args.bearer ? { bearer: args.bearer } : {}),
        });
    },
};

// ─── Encounters ──────────────────────────────────────────────────────────

// Work-list rows fold the patient join from the server-side select in
// encountersRouter.get('/work-list'). `patient` may be null if the FK ever
// dangles, but in practice every encounter has a patient row.
export interface WorkListEncounter extends Encounter {
    patient: { id: string; full_name: string | null; phone_e164: string } | null;
}

export interface EncounterDetailRes {
    encounter: Encounter;
    observations: Observation[];
    diagnoses: Diagnosis[];
    medication_requests: MedicationRequest[];
    service_requests: ServiceRequest[];
    // Only present when encounter.status === 'completed' (Week 5). The
    // server renders this from the sub-resources so the clinician sees the
    // same summary that was pushed to the patient's Pamosi Health app.
    visit_summary?: { text: string; oneLine: string };
    // Phase 3 — denormalized so EncounterDetail can gate the "Admit to
    // observation" button without a second fetch.
    open_admission?: AdmissionView | null;
}

export const encountersApi = {
    workList() {
        return request<{ encounters: WorkListEncounter[] }>(
            '/clinician/v1/encounters/work-list',
        );
    },
    detail(id: string) {
        return request<EncounterDetailRes>(`/clinician/v1/encounters/${id}`);
    },
    create(args: {
        patientId: string;
        encounterType: EncounterType;
        chiefComplaint?: string;
        // Skip the consent gate — used for walk-ins that aren't on Pamosi
        // yet or when a poor-network clinic can't wait on the patient ack.
        bypassConsent?: boolean;
        departmentId?:string;
    }) {
        return request<{ encounter: Encounter }>('/clinician/v1/encounters', {
            method: 'POST',
            body: JSON.stringify({
                patientId: args.patientId,
                ...(args.departmentId?{departmentId:args.departmentId}:{}),
                encounterType: args.encounterType,
                ...(args.chiefComplaint ? { chiefComplaint: args.chiefComplaint } : {}),
                ...(args.bypassConsent ? { bypassConsent: true } : {}),
            }),
        });
    },
    // PATCH the header fields (chief complaint, notes, encounter type).
    // Server returns { encounter } — either the updated row or, if the patch
    // was a no-op, the unchanged row. 409 on completed/cancelled encounters.
    update(id: string, patch: {
        chiefComplaint?: string | null;
        notes?: string | null;
        encounterType?: EncounterType;
    }) {
        return request<{ encounter: Encounter }>(`/clinician/v1/encounters/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        });
    },
    // Record one observation. `code` is a free-form clinical concept
    // (`systolic_bp`, `diastolic_bp`, `temp_c`, `weight_kg`, `pulse_bpm`,
    // etc.). Either valueNumeric or valueText must be non-null.
    addObservation(id: string, obs: {
        code: string;
        valueNumeric?: number | null;
        valueText?: string | null;
        unit?: string | null;
    }) {
        return request<{ observation: Observation }>(
            `/clinician/v1/encounters/${id}/observations`,
            { method: 'POST', body: JSON.stringify(obs) },
        );
    },
    addDiagnosis(id: string, dx: {
        description: string;
        code?: string | null;
        isPrimary?: boolean;
    }) {
        return request<{ diagnosis: Diagnosis }>(
            `/clinician/v1/encounters/${id}/diagnoses`,
            { method: 'POST', body: JSON.stringify(dx) },
        );
    },
    // Order a lab / imaging / procedure / referral. Server allowlist:
    // 'lab' | 'imaging' | 'procedure' | 'referral'. `description` is
    // required and free-form; `code` (e.g. LOINC/CPT) and `target` (external
    // lab, department, referred clinic) are optional.
    addServiceRequest(id: string, sr: {
        serviceType: 'lab' | 'imaging' | 'procedure' | 'referral';
        description: string;
        code?: string | null;
        target?: string | null;
    }) {
        return request<{ service_request: ServiceRequest }>(
            `/clinician/v1/encounters/${id}/service-requests`,
            { method: 'POST', body: JSON.stringify(sr) },
        );
    },
    addMedication(id: string, rx: {
        medicationName: string;
        dose?: string | null;
        route?: string | null;
        frequency?: string | null;
        duration?: string | null;
        instructions?: string | null;
        dispenseQty?: string | null;
        refills?: number;
    }) {
        return request<{ medication_request: MedicationRequest }>(
            `/clinician/v1/encounters/${id}/medications`,
            { method: 'POST', body: JSON.stringify(rx) },
        );
    },
    // Idempotent on the server (completed → returns the same row unchanged).
    // We invalidate the detail + work-list queries after so the row drops
    // off the work list and the detail screen re-renders the status pill.
    // Week 5: the response also carries the rendered visit summary + a
    // one-line preview (only on the first finalize; the idempotent re-call
    // for an already-completed encounter omits it).
    finalize(id: string) {
        return request<{
            encounter: Encounter;
            visit_summary?: { text: string; oneLine: string };
        }>(
            `/clinician/v1/encounters/${id}/finalize`,
            { method: 'POST' },
        );
    },
    // Cancel an in-progress encounter (no-show, wrong patient, etc.).
    // Optional reason is appended to the encounter notes on the server so
    // there's an audit trail without a schema change.
    cancel(id: string, opts: { reason?: string } = {}) {
        const reason = opts.reason?.trim();
        return request<{ encounter: Encounter }>(
            `/clinician/v1/encounters/${id}/cancel`,
            {
                method: 'POST',
                ...(reason ? { body: JSON.stringify({ reason }) } : {}),
            },
        );
    },
};

// ─── Patient lookup + consent request ────────────────────────────────────

export interface PatientLookupRes {
    patient: {
        id: string;
        phone_e164: string;
        full_name: string | null;
        identity_tier: string;
        nin_last4: string | null;
    };
    // Server sets true when the row was created on this lookup — clinic UI
    // uses it to badge walk-ins vs. existing Pamosi patients.
    is_new: boolean;
}

// ─── Pharmacy queue ──────────────────────────────────────────────────────

// Denorm'd shape from GET /pharmacy/queue: MedicationRequest + trimmed
// encounter + patient. Kept loose because the server picks specific fields
// with a nested select and mixing full row types would over-promise.
//
// Phase 3.b: `dispenses` ships the partial-dispense history so the row can
// render a running total and compute a remainder when `dispense_qty` begins
// with a number. Only partial, non-superseded rows appear here on an open
// queue entry — a final dispense would have removed the script from the
// queue entirely.
export interface PharmacyQueueEntry extends MedicationRequest {
    encounter: {
        id: string;
        clinic_id: string;
        encounter_type: string;
        started_at: string;
    } | null;
    patient: {
        id: string;
        full_name: string | null;
        phone_e164: string;
    } | null;
    dispenses?: Array<{
        id: string;
        superseded_by_id: string | null;
        is_partial: boolean;
        quantity: number | null;
        dispensed_at: string;
        note: string | null;
    }>;
}

export const pharmacyApi = {
    queue() {
        return request<{ prescriptions: PharmacyQueueEntry[] }>(
            '/clinician/v1/pharmacy/queue',
        );
    },
    // Week 6 — dedicated dispense event (migration 0080). Old PATCH endpoint
    // is gone; the queue now derives "dispensed" from the presence of a
    // non-superseded, non-partial dispense row.
    //
    // Phase 3.b (migration 0102): `is_partial: true` records a partial
    // dispense; the script stays on the queue until a final dispense
    // (is_partial=false) is written. The server requires a numeric
    // `quantity` whenever `is_partial` is true.
    dispense(id: string, args: { quantity?: number; note?: string; is_partial?: boolean } = {}) {
        return request<{ dispense: MedicationDispense }>(
            `/clinician/v1/pharmacy/prescriptions/${id}/dispenses`,
            {
                method: 'POST',
                body: JSON.stringify({
                    ...(args.quantity != null ? { quantity: args.quantity } : {}),
                    ...(args.note ? { note: args.note } : {}),
                    ...(args.is_partial ? { is_partial: true } : {}),
                }),
            },
        );
    },
    cancel(id: string, args: { reason?: string } = {}) {
        return request<{ prescription: MedicationRequest }>(
            `/clinician/v1/pharmacy/prescriptions/${id}/cancel`,
            {
                method: 'POST',
                body: JSON.stringify(args.reason ? { reason: args.reason } : {}),
            },
        );
    },
};

// Row shape returned by GET /encounters/clinic-patients. Not exported as a
// shared type because it lives entirely on the clinic side — no mobile
// client will consume it.
export interface ClinicPatientListEntry {
    id: string;
    phone_e164: string;
    full_name: string | null;
    identity_tier: string;
    last_encounter: { started_at: string; encounter_type: string } | null;
    hospital_numbers: string[];
    registration_status: 'draft' | 'submitted' | 'not_started';
    on_pamosi: boolean;
}

export interface PatientHistoryEncounter extends Encounter {
    clinician: {
        id: string;
        full_name: string | null;
        role: string;
    } | null;
}

export interface PatientHistoryRes {
    patient: {
        id: string;
        phone_e164: string;
        full_name: string | null;
        identity_tier: string;
        on_pamosi: boolean;
    };
    encounters: PatientHistoryEncounter[];
}

export const patientsApi = {
    // fullName is optional — sent when the clinician typed a name for a
    // walk-in so the server can backfill the patient row (never overwrites
    // an existing name).
    lookup(phoneE164: string, opts: { fullName?: string } = {}) {
        const name = opts.fullName?.trim();
        return request<PatientLookupRes>('/clinician/v1/patient-lookup', {
            method: 'POST',
            body: JSON.stringify({
                phone_e164: phoneE164,
                ...(name ? { full_name: name } : {}),
            }),
        });
    },
    // Patient records tab — clinic-wide list, optional search filter.
    listClinicPatients(opts: { search?: string; offset?: number } = {}) {
        const q = opts.search?.trim();
        const params = new URLSearchParams({ offset: String(opts.offset ?? 0) });
        if (q) params.set('search', q);
        const qs = `?${params}`;
        return request<{ patients: ClinicPatientListEntry[]; has_more: boolean; offset: number }>(
            `/clinician/v1/encounters/clinic-patients${qs}`,
        );
    },
    // Detail view — patient's encounter history at this clinic.
    encounterHistory(patientId: string) {
        return request<PatientHistoryRes>(
            `/clinician/v1/encounters/clinic-patients/${patientId}/history`,
        );
    },
    // Send an in-app consent request. Clinician gets consent_required back
    // from the encounter POST when the patient hasn't granted access —
    // this fires a WA push to the patient asking them to approve. The
    // response's `request.request_group_id` is used by the New Encounter
    // screen to poll for the patient's accept/decline (see
    // getConsentRequestStatus below).
    requestAccess(args: {
        phoneE164: string;
        dataCategories: string[];
        message?: string;
    }) {
        return request<{
            request: {
                request_group_id: string;
                status: 'pending' | 'active' | 'declined' | 'expired';
                expires_at: string;
            };
            qr?: { token: string; expires_at: string };
        }>(
            `/clinician/v1/patients/${encodeURIComponent(args.phoneE164)}/request-access`,
            {
                method: 'POST',
                body: JSON.stringify({
                    data_categories: args.dataCategories,
                    ...(args.message ? { message: args.message } : {}),
                }),
            },
        );
    },
    // Poll a single consent request by group id — used by the New Encounter
    // screen to auto-react when the patient accepts or declines. Returns
    // 404 if the caller isn't the recipient (server keys off recipient_id).
    getConsentRequestStatus(groupId: string) {
        return request<{
            request_group_id: string;
            status: 'pending' | 'active' | 'declined' | 'expired';
            expires_at: string;
        }>(`/clinician/v1/consent/requests/${encodeURIComponent(groupId)}`);
    },
};

// Reception (Phase 1b of the revised EMR, 2026-10-03). Search is a GET so
// the browser's back/forward preserves the query; create is a POST that
// returns the full bundle (patient + card + draft registration + any HMO).
export const receptionApi = {
    registrationOnline:(id:string)=>request<ReceptionRegistrationBundleRes>('/clinician/v1/reception/patients/'+id+'/registration',{onlineOnly:true}),
    search(q: string) {
        const qs = q.trim().length === 0 ? '' : `?q=${encodeURIComponent(q.trim())}`;
        return request<ReceptionPatientSearchRes>(
            `/clinician/v1/reception/patients/search${qs}`,
        );
    },
    createPatient(args: {
        clientRequestId?:string;
        phoneE164: string;
        fullName?: string;
        cardType: PatientCardType;
        hospitalNumber?: string;
        familyId?: string;
        notes?: string;
    }) {
        return request<ReceptionCreatePatientRes>(
            `/clinician/v1/reception/patients`,
            {
                method: 'POST',
                body: JSON.stringify({
                    phone_e164: args.phoneE164,
                    ...(args.clientRequestId?{client_request_id:args.clientRequestId}:{}),
                    card_type: args.cardType,
                    ...(args.fullName ? { full_name: args.fullName } : {}),
                    ...(args.hospitalNumber ? { hospital_number: args.hospitalNumber } : {}),
                    ...(args.familyId ? { family_id: args.familyId } : {}),
                    ...(args.notes ? { notes: args.notes } : {}),
                }),
            },
        );
    },
    getRegistrationBundle(patientId: string) {
        return request<ReceptionRegistrationBundleRes>(
            `/clinician/v1/reception/patients/${encodeURIComponent(patientId)}/registration`,
        );
    },
    updateRegistration(patientId: string, patch: ReceptionUpdateRegistrationReq) {
        return request<PatientRegistrationDetails>(
            `/clinician/v1/reception/patients/${encodeURIComponent(patientId)}/registration`,
            { method: 'PATCH', body: JSON.stringify(patch) },
        );
    },
    listHmoProviders(search?: string) {
        const qs = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
        return request<ReceptionHmoProvidersRes>(
            `/clinician/v1/reception/hmo-providers${qs}`,
        );
    },
    upsertHmo(patientId: string, args: {
        hmoProviderId: string;
        policyNumber?: string;
        planName?: string;
        memberReference?: string;
        authorizationReference?: string;
        notes?: string;
    }) {
        return request<PatientHmoCoverage>(
            `/clinician/v1/reception/patients/${encodeURIComponent(patientId)}/hmo`,
            {
                method: 'POST',
                body: JSON.stringify({
                    hmo_provider_id: args.hmoProviderId,
                    ...(args.policyNumber !== undefined ? { policy_number: args.policyNumber } : {}),
                    ...(args.planName !== undefined ? { plan_name: args.planName } : {}),
                    ...(args.memberReference !== undefined ? { member_reference: args.memberReference } : {}),
                    ...(args.authorizationReference !== undefined ? { authorization_reference: args.authorizationReference } : {}),
                    ...(args.notes !== undefined ? { notes: args.notes } : {}),
                }),
            },
        );
    },
    // ─── Family card (Phase 1c, 2026-10-03) ──────────────────────────────
    searchFamilies(q: string) {
        const qs = q.trim().length === 0 ? '' : `?q=${encodeURIComponent(q.trim())}`;
        return request<ReceptionFamilySearchRes>(
            `/clinician/v1/reception/families/search${qs}`,
        );
    },
    createFamily(args: {
        familyName: string;
        accountNumber?: string;
        address?: string;
        mainContactName?: string;
        mainContactPhoneE164?: string;
        mainContactEmail?: string;
        defaultHmoProviderId?: string;
    }) {
        return request<ReceptionCreateFamilyRes>(
            `/clinician/v1/reception/families`,
            {
                method: 'POST',
                body: JSON.stringify({
                    family_name: args.familyName,
                    ...(args.accountNumber ? { account_number: args.accountNumber } : {}),
                    ...(args.address ? { address: args.address } : {}),
                    ...(args.mainContactName ? { main_contact_name: args.mainContactName } : {}),
                    ...(args.mainContactPhoneE164 ? { main_contact_phone_e164: args.mainContactPhoneE164 } : {}),
                    ...(args.mainContactEmail ? { main_contact_email: args.mainContactEmail } : {}),
                    ...(args.defaultHmoProviderId ? { default_hmo_provider_id: args.defaultHmoProviderId } : {}),
                }),
            },
        );
    },
    getFamilyBundle(familyId: string) {
        return request<ReceptionFamilyBundleRes>(
            `/clinician/v1/reception/families/${encodeURIComponent(familyId)}`,
        );
    },
    addBeneficiary(familyId: string, args: {
        phoneE164: string;
        fullName?: string;
        relationship?: string;
        isAccountHolder?: boolean;
        usesOwnContact?: boolean;
    }) {
        return request<ReceptionAddBeneficiaryRes>(
            `/clinician/v1/reception/families/${encodeURIComponent(familyId)}/beneficiaries`,
            {
                method: 'POST',
                body: JSON.stringify({
                    phone_e164: args.phoneE164,
                    ...(args.fullName ? { full_name: args.fullName } : {}),
                    ...(args.relationship ? { relationship: args.relationship } : {}),
                    ...(args.isAccountHolder ? { is_account_holder: true } : {}),
                    ...(args.usesOwnContact ? { uses_own_contact: true } : {}),
                }),
            },
        );
    },

    // ─── Departments + enqueue (Phase 2a, 2026-10-03) ────────────────────
    listDepartments() {
        return request<ReceptionDepartmentsRes>(
            `/clinician/v1/reception/departments`,
        );
    },
    enqueue(patientId: string, args: {
        departmentId: string;
        patientCardId?: string;
        chiefComplaint?: string;
        priority?: number;
    }) {
        return request<ReceptionEnqueueRes>(
            `/clinician/v1/reception/patients/${encodeURIComponent(patientId)}/enqueue`,
            {
                method: 'POST',
                body: JSON.stringify({
                    department_id: args.departmentId,
                    ...(args.patientCardId ? { patient_card_id: args.patientCardId } : {}),
                    ...(args.chiefComplaint ? { chief_complaint: args.chiefComplaint } : {}),
                    ...(typeof args.priority === 'number' ? { priority: args.priority } : {}),
                }),
            },
        );
    },
    // HMO verification — original-chart Phase 3.c₂ (2026-10-04)
    verifyHmo(patientId: string, hmoId: string, body: VerifyHmoCoverageRequest) {
        return request<PatientHmoCoverage>(
            `/clinician/v1/reception/patients/${encodeURIComponent(patientId)}/hmo/${encodeURIComponent(hmoId)}/verify`,
            { method: 'PATCH', body: JSON.stringify(body) },
        );
    },
};

// Queues (Phase 2a, 2026-10-03). List is clinic-wide with optional dept
// filter; status updates drive the pick-up / done / reroute lifecycle.
export const queuesApi = {
    list(opts: { departmentId?: string } = {}) {
        const qs = opts.departmentId
            ? `?department_id=${encodeURIComponent(opts.departmentId)}`
            : '';
        return request<QueueListRes>(`/clinician/v1/queues${qs}`);
    },
    updateStatus(entryId: string, args: {
        status: VisitQueueStatus;
        encounterId?: string;
    }) {
        return request<{ entry: QueueUpdateStatusRes }>(
            `/clinician/v1/queues/${encodeURIComponent(entryId)}/status`,
            {
                method: 'PATCH',
                body: JSON.stringify({
                    status: args.status,
                    ...(args.encounterId ? { encounter_id: args.encounterId } : {}),
                }),
            },
        );
    },
};

// Shared instructions (Phase 2b, 2026-10-03). Patient-level staff notes
// surfaced on reception + encounter screens. All routes keyed by patientId
// so the UI never confuses one patient's notes for another.
export const instructionsApi = {
    list(patientId: string, opts: { includeResolved?: boolean } = {}) {
        const qs = opts.includeResolved ? '?include_resolved=true' : '';
        return request<SharedInstructionsListRes>(
            `/clinician/v1/patients/${encodeURIComponent(patientId)}/instructions${qs}`,
        );
    },
    create(patientId: string, args: { body: string; pinned?: boolean;clientRequestId?:string }) {
        return request<SharedInstructionRes>(
            `/clinician/v1/patients/${encodeURIComponent(patientId)}/instructions`,
            {
                method: 'POST',
                body: JSON.stringify({
                    body: args.body,
                    ...(args.clientRequestId?{client_request_id:args.clientRequestId}:{}),
                    ...(args.pinned ? { pinned: true } : {}),
                }),
            },
        );
    },
    update(patientId: string, instructionId: string, args: {
        body?: string;
        pinned?: boolean;
        resolved?: boolean;
    }) {
        return request<SharedInstructionRes>(
            `/clinician/v1/patients/${encodeURIComponent(patientId)}/instructions/${encodeURIComponent(instructionId)}`,
            {
                method: 'PATCH',
                body: JSON.stringify(args),
            },
        );
    },
    remove(patientId: string, instructionId: string) {
        return request<{ ok: true }>(
            `/clinician/v1/patients/${encodeURIComponent(patientId)}/instructions/${encodeURIComponent(instructionId)}`,
            { method: 'DELETE' },
        );
    },
};

// Inbox (Phase 2c, 2026-10-03). Lists @mentions aimed at the authed clinician
// plus the autocomplete roster. Mutation endpoints are per-caller — the server
// scopes writes to mentioned_clinician_id so one clinician can't flip
// another's inbox.
export const inboxApi = {
    list(opts: { limit?: number } = {}) {
        const qs = opts.limit ? `?limit=${encodeURIComponent(opts.limit)}` : '';
        return request<InboxListRes>(`/clinician/v1/inbox${qs}`);
    },
    candidates() {
        return request<MentionCandidatesRes>('/clinician/v1/inbox/candidates');
    },
    markRead(mentionId: string) {
        return request<{ ok: true }>(
            `/clinician/v1/inbox/${encodeURIComponent(mentionId)}/read`,
            { method: 'POST' },
        );
    },
    clear(mentionId: string) {
        return request<{ ok: true }>(
            `/clinician/v1/inbox/${encodeURIComponent(mentionId)}/clear`,
            { method: 'POST' },
        );
    },
};

// Triage (Phase 2d, 2026-10-03). Vitals-first nurse intake on an encounter.
// GET returns null body (404) when no triage row exists yet — the nurse's
// first save creates it. PATCH is the single save-or-complete endpoint;
// pass `complete: true` + `next_department_id` to re-queue onto a clinical
// department after vitals are captured.
export const triageApi = {
    get(encounterId: string) {
        return request<TriageRes>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/triage`,
        );
    },
    upsert(encounterId: string, body: TriageUpsertReq) {
        return request<TriageRes>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/triage`,
            {
                method: 'PATCH',
                body: JSON.stringify(body),
            },
        );
    },
};

// Procedures (Phase 2e, 2026-10-03). Nurse execution of doctor-written
// service_requests of type 'procedure'. Fulfillments are N-per-request so
// a multi-session order (3-day dressing, series of injections) records
// each execution independently.
export const proceduresApi = {
    queue(opts: { includeClosed?: boolean; hideMine?: boolean } = {}) {
        const parts: string[] = [];
        if (opts.includeClosed) parts.push('include_closed=true');
        if (opts.hideMine) parts.push('hide_mine=true');
        const qs = parts.length ? `?${parts.join('&')}` : '';
        return request<ProceduresQueueRes>(`/clinician/v1/procedures/queue${qs}`);
    },
    listFulfillments(serviceRequestId: string) {
        return request<ServiceRequestFulfillmentsListRes>(
            `/clinician/v1/procedures/${encodeURIComponent(serviceRequestId)}/fulfillments`,
        );
    },
    fulfill(serviceRequestId: string, body: ServiceRequestFulfillmentReq) {
        return request<ServiceRequestFulfillmentRes>(
            `/clinician/v1/procedures/${encodeURIComponent(serviceRequestId)}/fulfillments`,
            {
                method: 'POST',
                body: JSON.stringify(body),
            },
        );
    },
    close(serviceRequestId: string, body: ServiceRequestCloseReq = {}) {
        return request<{ ok: true }>(
            `/clinician/v1/procedures/${encodeURIComponent(serviceRequestId)}/close`,
            {
                method: 'POST',
                body: JSON.stringify(body),
            },
        );
    },
    cancel(serviceRequestId: string, body: ServiceRequestCancelReq = {}) {
        return request<{ ok: true }>(
            `/clinician/v1/procedures/${encodeURIComponent(serviceRequestId)}/cancel`,
            {
                method: 'POST',
                body: JSON.stringify(body),
            },
        );
    },
    // Phase 3 — multi-photo flow per fulfillment. Caller:
    //   1. signPhotoUpload(sr, fid, { content_type })  → { photo_id, signed_url, storage_path }
    //   2. PUT the compressed blob to signed_url (lives in the UI, needs the File)
    //   3. attachPhoto(sr, fid, photo_id)              → { photo: FulfillmentPhotoView }
    // Soft-delete via deletePhoto(sr, fid, photo_id) also hard-deletes the
    // Storage object server-side — the 7-day signed GET URL otherwise
    // leaks a wrong-patient photo until TTL expiry.
    signPhotoUpload(
        serviceRequestId: string,
        fulfillmentId: string,
        body: ProcedurePhotoSignUploadReq,
    ) {
        return request<ProcedurePhotoSignUploadRes>(
            `/clinician/v1/procedures/${encodeURIComponent(serviceRequestId)}/fulfillments/${encodeURIComponent(fulfillmentId)}/photos`,
            {
                method: 'POST',
                body: JSON.stringify(body),
            },
        );
    },
    attachPhoto(
        serviceRequestId: string,
        fulfillmentId: string,
        photoId: string,
    ) {
        return request<ProcedurePhotoAttachRes>(
            `/clinician/v1/procedures/${encodeURIComponent(serviceRequestId)}/fulfillments/${encodeURIComponent(fulfillmentId)}/photos/${encodeURIComponent(photoId)}/attach`,
            {
                method: 'POST',
            },
        );
    },
    deletePhoto(
        serviceRequestId: string,
        fulfillmentId: string,
        photoId: string,
    ) {
        return request<{ ok: true }>(
            `/clinician/v1/procedures/${encodeURIComponent(serviceRequestId)}/fulfillments/${encodeURIComponent(fulfillmentId)}/photos/${encodeURIComponent(photoId)}`,
            {
                method: 'DELETE',
            },
        );
    },
};

// Admissions (Phase 3, 2026-10-03). Observation / holding-area primitive.
// Lifecycle: admit → (vitals re-takes via encounters.observations) → discharge
// (with disposition) or cancel (nurse mis-tap recovery).
export const admissionsApi = {
    list(opts: { status?: AdmissionStatus } = {}) {
        const qs = opts.status ? `?status=${encodeURIComponent(opts.status)}` : '';
        return request<AdmissionsListRes>(`/clinician/v1/admissions${qs}`);
    },
    get(id: string) {
        return request<AdmissionRes>(`/clinician/v1/admissions/${encodeURIComponent(id)}`);
    },
    admit(body: AdmitRequest) {
        return request<AdmitResponse>(`/clinician/v1/admissions`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
    },
    discharge(id: string, body: DischargeRequest) {
        return request<AdmissionRes>(
            `/clinician/v1/admissions/${encodeURIComponent(id)}/discharge`,
            { method: 'PATCH', body: JSON.stringify(body) },
        );
    },
    cancel(id: string, body: CancelAdmissionRequest = {}) {
        return request<AdmissionRes>(
            `/clinician/v1/admissions/${encodeURIComponent(id)}/cancel`,
            { method: 'PATCH', body: JSON.stringify(body) },
        );
    },
    setVitalsSchedule(id: string, body: SetVitalsScheduleRequest) {
        return request<AdmissionRes>(
            `/clinician/v1/admissions/${encodeURIComponent(id)}/vitals-schedule`,
            { method: 'PATCH', body: JSON.stringify(body) },
        );
    },
    // Phase 4 — observation chart (reads existing observations) + MAR.
    observationsChart(id: string) {
        return request<AdmissionObservationChartRes>(
            `/clinician/v1/admissions/${encodeURIComponent(id)}/observations-chart`,
        );
    },
    mar(id: string) {
        return request<AdmissionMarRes>(
            `/clinician/v1/admissions/${encodeURIComponent(id)}/mar`,
        );
    },
    administer(id: string, body: RecordAdministrationRequest) {
        return request<AdministrationRes>(
            `/clinician/v1/admissions/${encodeURIComponent(id)}/mar/administrations`,
            { method: 'POST', body: JSON.stringify(body) },
        );
    },
};

// ─── Shift reports API (Phase 4) ─────────────────────────────────────────

export const shiftReportsApi = {
    list() {
        return request<ShiftReportsListRes>(`/clinician/v1/shift-reports`);
    },
    get(id: string) {
        return request<ShiftReportRes>(
            `/clinician/v1/shift-reports/${encodeURIComponent(id)}`,
        );
    },
    create(body: CreateShiftReportRequest) {
        return request<ShiftReportRes>(`/clinician/v1/shift-reports`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
    },
    update(id: string, body: UpdateShiftReportRequest) {
        return request<ShiftReportRes>(
            `/clinician/v1/shift-reports/${encodeURIComponent(id)}`,
            { method: 'PATCH', body: JSON.stringify(body) },
        );
    },
};

// ─── Labs API — original-chart Phase 3.a (2026-10-04) ────────────────────

export const labsApi = {
    // Catalogue
    listCatalogue(opts: { includeInactive?: boolean } = {}) {
        const qs = opts.includeInactive ? '?include_inactive=true' : '';
        return request<LabTestsListRes>(`/clinician/v1/labs/catalogue${qs}`);
    },
    upsertCatalogue(body: UpsertLabTestRequest) {
        return request<LabTestRes>(`/clinician/v1/labs/catalogue`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
    },
    deleteCatalogue(id: string) {
        return request<{ test: LabTest; deactivated: boolean }>(
            `/clinician/v1/labs/catalogue/${encodeURIComponent(id)}`,
            { method: 'DELETE' },
        );
    },
    // Queue
    listQueue(opts: { status?: LabStatus; includeClosed?: boolean; patientId?: string } = {}) {
        const params = new URLSearchParams();
        if (opts.status) params.set('status', opts.status);
        if (opts.includeClosed) params.set('include_closed', 'true');
        if (opts.patientId) params.set('patient_id', opts.patientId);
        const qs = params.toString() ? `?${params.toString()}` : '';
        return request<LabsQueueRes>(`/clinician/v1/labs/queue${qs}`);
    },
    getRequest(id: string) {
        return request<LabRequestRes>(`/clinician/v1/labs/queue/${encodeURIComponent(id)}`);
    },
    // Workflow
    order(body: OrderLabRequest) {
        return request<LabRequestRes>(`/clinician/v1/labs/orders`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
    },
    collect(id: string, note?: string | null) {
        return request<LabRequestRes>(
            `/clinician/v1/labs/queue/${encodeURIComponent(id)}/collect`,
            { method: 'PATCH', body: JSON.stringify({ note: note ?? null }) },
        );
    },
    process(id: string, note?: string | null) {
        return request<LabRequestRes>(
            `/clinician/v1/labs/queue/${encodeURIComponent(id)}/process`,
            { method: 'PATCH', body: JSON.stringify({ note: note ?? null }) },
        );
    },
    enterResult(id: string, body: EnterLabResultRequest) {
        return request<LabRequestRes>(
            `/clinician/v1/labs/queue/${encodeURIComponent(id)}/result`,
            { method: 'PATCH', body: JSON.stringify(body) },
        );
    },
    release(id: string, body: ReleaseLabRequest = {}) {
        return request<LabRequestRes>(
            `/clinician/v1/labs/queue/${encodeURIComponent(id)}/release`,
            { method: 'PATCH', body: JSON.stringify(body) },
        );
    },
    cancel(id: string, body: CancelLabRequest) {
        return request<LabRequestRes>(
            `/clinician/v1/labs/queue/${encodeURIComponent(id)}/cancel`,
            { method: 'PATCH', body: JSON.stringify(body) },
        );
    },
};
export type { LabRequestView };

// ─── Charges API — original-chart Phase 3.c₁ (2026-10-04) ────────────────

export const chargesApi = {
    list(encounterId: string) {
        return request<{ charges: EncounterCharge[] }>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/charges`,
        );
    },
    create(encounterId: string, body: CreateEncounterChargeRequest) {
        return request<{ charge: EncounterCharge }>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/charges`,
            { method: 'POST', body: JSON.stringify(body) },
        );
    },
    markPaid(encounterId: string, chargeId: string) {
        return request<{ charge: EncounterCharge }>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/charges/${encodeURIComponent(chargeId)}/mark-paid`,
            { method: 'PATCH', body: JSON.stringify({}) },
        );
    },
    void(encounterId: string, chargeId: string, reason?: string | null) {
        return request<{ charge: EncounterCharge }>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/charges/${encodeURIComponent(chargeId)}/void`,
            { method: 'PATCH', body: JSON.stringify({ reason: reason ?? null }) },
        );
    },
};

// ─── Procedure catalogue API — original-chart Phase 3.c₂ (2026-10-04) ────

export const procedureCatalogueApi = {
    list(opts: { includeInactive?: boolean } = {}) {
        const qs = opts.includeInactive ? '?include_inactive=true' : '';
        return request<ClinicProceduresListRes>(`/clinician/v1/procedures/catalogue${qs}`);
    },
    upsert(body: UpsertClinicProcedureRequest) {
        return request<ClinicProcedureRes>(`/clinician/v1/procedures/catalogue`, {
            method: 'POST',
            body: JSON.stringify(body),
        });
    },
    delete(id: string) {
        return request<{ procedure: ClinicProcedure; deactivated: boolean }>(
            `/clinician/v1/procedures/catalogue/${encodeURIComponent(id)}`,
            { method: 'DELETE' },
        );
    },
};

// ─── Encounter documents API — original-chart Phase 3.c₂ (2026-10-04) ────
// Receipt / prescription / lab-request PDFs + receipt delivery (Vault/email).

export interface EncounterDocumentPdfRes {
    signed_url: string;
    storage_path: string;
    expires_at: string;
}

export interface EncounterReceiptDeliverRes {
    signed_url: string;
    storage_path: string;
    channel: 'vault' | 'email';
    delivered_to?: string | null;
}

export const encounterDocumentsApi = {
    renderReceiptPdf(encounterId: string) {
        return request<EncounterDocumentPdfRes>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/documents/receipt/pdf`,
            { method: 'POST', body: JSON.stringify({}) },
        );
    },
    deliverReceipt(
        encounterId: string,
        body: { channel: 'vault' | 'email'; email?: string | null },
    ) {
        return request<EncounterReceiptDeliverRes>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/documents/receipt/deliver`,
            { method: 'POST', body: JSON.stringify(body) },
        );
    },
    renderPrescriptionPdf(encounterId: string) {
        return request<EncounterDocumentPdfRes>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/documents/prescription/pdf`,
            { method: 'POST', body: JSON.stringify({}) },
        );
    },
    renderLabRequestPdf(encounterId: string) {
        return request<EncounterDocumentPdfRes>(
            `/clinician/v1/encounters/${encodeURIComponent(encounterId)}/documents/lab-request/pdf`,
            { method: 'POST', body: JSON.stringify({}) },
        );
    },
};

export const clinicPaymentsApi = {
 operations:()=>request<{payments:any[];refunds:any[];credits:any[];checks:any[]}>('/clinician/v1/clinic-payments/operations'),
 requestRefund:(body:unknown)=>request('/clinician/v1/clinic-payments/operations/refunds',{method:'POST',body:JSON.stringify(body)}),
 credit:(body:unknown)=>request('/clinician/v1/clinic-payments/operations/credits',{method:'POST',body:JSON.stringify(body)}),
 submitRefund:(id:string)=>request(`/clinician/v1/clinic-payments/operations/refunds/${id}/submit`,{method:'POST',body:JSON.stringify({confirmed:true})}),
 checkRefund:(id:string)=>request(`/clinician/v1/clinic-payments/operations/refunds/${id}/check`,{method:'POST',body:'{}'}),
 manualRefund:(id:string,payout_reference:string)=>request(`/clinician/v1/clinic-payments/operations/refunds/${id}/manual`,{method:'POST',body:JSON.stringify({confirmed:true,payout_reference})}),
 settlementCheck:(from:string,to:string)=>request('/clinician/v1/clinic-payments/operations/settlements/check',{method:'POST',body:JSON.stringify({from,to})}),
 setup: () => request<import('@pamosi/shared').ClinicPaymentSetup>('/clinician/v1/clinic-payments/setup'),
 banks: () => request<{banks:Array<{code:string;name:string}>}>('/clinician/v1/clinic-payments/banks'),
 configure: (body:{bank_code:string;account_number:string;confirm_test_setup:true}) => request('/clinician/v1/clinic-payments/setup',{method:'POST',body:JSON.stringify(body)}),
 list: () => request<{invoices:import('@pamosi/shared').ClinicInvoice[]}>('/clinician/v1/clinic-payments/invoices'),
 encounter: (id:string) => request<{invoices:import('@pamosi/shared').ClinicInvoice[]}>(`/clinician/v1/clinic-payments/encounters/${id}`),
 prepare: (id:string) => request<{invoice:import('@pamosi/shared').ClinicInvoice}>(`/clinician/v1/clinic-payments/encounters/${id}/invoices`,{method:'POST',body:'{}'}),
 cancel: (id:string) => request(`/clinician/v1/clinic-payments/invoices/${id}/cancel`,{method:'POST',body:'{}'}),
 manual: (id:string,body:{amount_kobo:number;method:string;idempotency_key:string;confirmed:boolean}) => request(`/clinician/v1/clinic-payments/invoices/${id}/manual`,{method:'POST',body:JSON.stringify(body)}),
 verifyInvoice: (id:string) => request<{status:string}>(`/clinician/v1/clinic-payments/invoices/${id}/verify`,{method:'POST',body:'{}'}),
 renew: (id:string) => request(`/clinician/v1/clinic-payments/invoices/${id}/link`,{method:'POST',body:'{}'}),
 audit: () => request<{entries:Array<{id:string;at:string;action:string;source:string;actor:{full_name:string|null}|null;metadata:Record<string,unknown>}>}>('/clinician/v1/clinic-payments/audit'),
 bill: (token:string) => request<import('@pamosi/shared').PatientBillResponse>(`/clinic-payments/v1/pay/${encodeURIComponent(token)}`,{auth:false}),
 checkout: (token:string,email:string,amount_kobo:number) => request<{authorization_url:string;reference:string;mode:'test'}>(`/clinic-payments/v1/pay/${encodeURIComponent(token)}/checkout`,{auth:false,method:'POST',body:JSON.stringify({email,amount_kobo})}),
 verify: (token:string,reference:string) => request<{status:string}>(`/clinic-payments/v1/pay/${encodeURIComponent(token)}/verify`,{auth:false,method:'POST',body:JSON.stringify({reference})}),
};

const childBase='/clinician/v1/child-health';
export const childHealthApi={
 patientLinks:(patient:string)=>request<{children:import('@pamosi/shared').ChildSummary[]}>(childBase+'/patient-links/'+patient),
 list:(mother?:string)=>request<{children:import('@pamosi/shared').ChildSummary[]}>(childBase+'/children'+(mother?'?mother='+encodeURIComponent(mother):'')),
 deliveries:(mother:string)=>request<{deliveries:import('@pamosi/shared').ClinicDelivery[]}>(childBase+'/deliveries?mother='+encodeURIComponent(mother)),
 recordDelivery:(body:unknown)=>request<{delivery:import('@pamosi/shared').ClinicDelivery}>(childBase+'/deliveries',{method:'POST',body:JSON.stringify(body)}),
 register:(body:import('@pamosi/shared').ChildRegistration)=>request<{profile:import('@pamosi/shared').ChildProfile}>(childBase+'/children',{method:'POST',body:JSON.stringify(body)}),
 get:(id:string)=>request<import('@pamosi/shared').ChildHealthBundle>(childBase+'/children/'+id),
 update:(id:string,body:import('@pamosi/shared').ChildContact & {expected_updated_at:string})=>request(childBase+'/children/'+id,{method:'PATCH',body:JSON.stringify(body)}),
 event:(id:string,body:unknown)=>request<{event:import('@pamosi/shared').ChildEvent}>(childBase+'/children/'+id+'/events',{method:'POST',body:JSON.stringify(body)}),
 followup:(id:string,body:unknown)=>request(childBase+'/children/'+id+'/followups',{method:'POST',body:JSON.stringify(body)})
};

export const offlineApi={
 prepare:(device_id:string)=>request<{lease:import('@pamosi/shared').OfflineLease;actor_name:string;clinic_name:string;departments:Array<{id:string;name:string}>}>('/clinician/v1/offline/prepare',{method:'POST',body:JSON.stringify({device_id})}),
 patient:(id:string,lease_id:string)=>request<{patient:import('@pamosi/shared').OfflinePatient}>('/clinician/v1/offline/patients/'+id+'?lease_id='+encodeURIComponent(lease_id)),
 commit:(operation:import('@pamosi/shared').OfflineOperation,payload:Record<string,unknown>,lease_id:string)=>request<{result:Record<string,unknown>}>('/clinician/v1/offline/commit',{method:'POST',body:JSON.stringify({id:operation.id,kind:operation.kind,captured_at:operation.captured_at,payload,lease_id})})
};

// Authorised, audited clinic snapshots gate automatic per-patient device caching.
setCacheAuthoriser((patient,lease)=>offlineApi.patient(patient,lease));
